import { clsx } from "clsx"
import { createContext, forwardRef, type ReactNode, useContext } from "react"

import type { SX } from "../../theme"
import styles from "./ButtonGroup.module.css"

export interface ButtonGroupProps {
  children?: ReactNode
  disabled?: boolean
  size?: "small" | "medium" | "large"
  color?: "grey" | "purple" | "dark"
  density?: "regular" | "compact"
  sx?: SX
}

export const ButtonGroup = forwardRef<HTMLDivElement, ButtonGroupProps>(
  function ButtonGroup(props, ref) {
    const buttonGroup = useButtonGroup()

    const {
      children,
      density = "regular",
      disabled = buttonGroup?.disabled,
      size = buttonGroup?.size ?? "medium",
      color = buttonGroup?.color ?? "purple",
      sx,
      ...otherProps
    } = props

    const value = { disabled, size, color }

    return (
      <ButtonGroupContext.Provider value={value}>
        <div
          {...otherProps}
          className={clsx(styles.buttonGroup, styles[`density-${density}`])}
          style={sx}
          ref={ref}
        >
          {children}
        </div>
      </ButtonGroupContext.Provider>
    )
  }
)

interface ButtonGroupContextValue {
  disabled?: boolean
  size: "small" | "medium" | "large"
  color: "grey" | "dark" | "purple"
}

const ButtonGroupContext = createContext<ButtonGroupContextValue | undefined>(
  undefined
)

export function useButtonGroup(): ButtonGroupContextValue | undefined {
  return useContext(ButtonGroupContext)
}
